if (!customElements.get('td-cart-cross-sell')) {
  class TDCartCrossSell extends HTMLElement {
    constructor() {
      super();
      this.requestController = null;
      this.pendingAddButton = null;
      this.handleTrackClick = this.handleTrackClick.bind(this);
      this.handleThemeLoadingEnd = this.handleThemeLoadingEnd.bind(this);
    }

    connectedCallback() {
      this.slider = this.querySelector('swiper-slider');
      this.track = this.querySelector('[data-td-cross-sell-track]');
      this.wrapper = this.querySelector('[data-td-cross-sell-wrapper]');

      if (!(this.slider instanceof HTMLElement) || !(this.track instanceof HTMLElement) || !(this.wrapper instanceof HTMLElement)) {
        this.setAttribute('hidden', '');
        return;
      }

      this.track.addEventListener('click', this.handleTrackClick);
      document.addEventListener('theme:loading:end', this.handleThemeLoadingEnd);

      if ((this.dataset.tdCrossSellSource || '') !== 'recommended') {
        const hasSlides = this.wrapper.querySelectorAll('[data-td-cross-sell-slide]').length > 0;
        this.toggleAttribute('hidden', !hasSlides);
        if (hasSlides) this.initializeSwiper();
        return;
      }

      if (!this.dataset.tdCrossSellSeedProductId) {
        this.setAttribute('hidden', '');
        this.wrapper.replaceChildren();
        return;
      }

      this.renderRecommendedCards();
    }

    disconnectedCallback() {
      if (this.requestController) {
        this.requestController.abort();
      }

      if (this.track instanceof HTMLElement) {
        this.track.removeEventListener('click', this.handleTrackClick);
      }

      document.removeEventListener('theme:loading:end', this.handleThemeLoadingEnd);
      this.pendingAddButton = null;
    }

    get maxProducts() {
      const parsed = Number.parseInt(this.dataset.tdCrossSellMaxProducts || '6', 10);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 6;
    }

    get cartProductIds() {
      const ids = new Set();
      const csv = this.dataset.tdCrossSellCartProductIds || '';

      csv.split(',').forEach((value) => {
        const parsed = Number.parseInt(value.trim(), 10);
        if (Number.isFinite(parsed) && parsed > 0) ids.add(parsed);
      });

      return ids;
    }

    get cartSignature() {
      return (this.dataset.tdCrossSellCartProductIds || '').replace(/\s+/g, '');
    }

    get recommendedCacheKey() {
      return [
        this.dataset.tdCrossSellSource || 'recommended',
        this.dataset.tdCrossSellSectionId || 'td-cart-cross-sell-cards',
        this.dataset.tdCrossSellSeedProductId || '',
        this.maxProducts,
        this.cartSignature,
      ].join('::');
    }

    get cacheStore() {
      // Shared cache is intentional so drawer re-renders can reuse identical recommendation responses.
      if (!window.__tdCartCrossSellRecommendedCache) {
        window.__tdCartCrossSellRecommendedCache = new Map();
      }

      return window.__tdCartCrossSellRecommendedCache;
    }

    handleTrackClick(event) {
      if (!(event.target instanceof Element)) return;

      const addButton = event.target.closest('[data-upsell-add]');
      if (!(addButton instanceof HTMLButtonElement)) return;

      this.pendingAddButton = addButton;
      this.pendingAddButton.setAttribute('aria-busy', 'true');
      this.pendingAddButton.disabled = true;
    }

    handleThemeLoadingEnd() {
      if (!(this.pendingAddButton instanceof HTMLButtonElement)) return;

      this.pendingAddButton.removeAttribute('aria-busy');
      this.pendingAddButton.disabled = false;
      this.pendingAddButton = null;
    }

    async renderRecommendedCards() {
      const seedProductId = Number.parseInt(this.dataset.tdCrossSellSeedProductId || '', 10);

      if (!(this.wrapper instanceof HTMLElement)) return;
      if (!Number.isFinite(seedProductId) || seedProductId <= 0) {
        this.setAttribute('hidden', '');
        this.wrapper.replaceChildren();
        return;
      }

      const cachedSlides = this.cacheStore.get(this.recommendedCacheKey);
      if (Array.isArray(cachedSlides)) {
        this.renderCardsFromMarkup(cachedSlides);
        return;
      }

      if (this.requestController) {
        this.requestController.abort();
      }
      this.requestController = new AbortController();

      try {
        const response = await fetch(this.recommendedSectionUrl, {
          headers: { Accept: 'text/html' },
          signal: this.requestController.signal,
        });

        if (!response.ok) throw new Error(`Cross-sell recommendations request failed: ${response.status}`);

        const html = await response.text();
        const slideMarkup = this.extractSlideMarkup(html);

        this.cacheStore.set(this.recommendedCacheKey, slideMarkup);
        this.trimCache();

        this.renderCardsFromMarkup(slideMarkup);
      } catch (error) {
        if (error?.name !== 'AbortError') {
          console.error(error);
          this.setAttribute('hidden', '');
        }
      }
    }

    get recommendedSectionUrl() {
      const routeRoot = window.Shopify?.routes?.root || '/';
      const url = new URL(`${routeRoot}recommendations/products`, window.location.origin);
      url.searchParams.set('section_id', this.dataset.tdCrossSellSectionId || 'td-cart-cross-sell-cards');
      url.searchParams.set('product_id', this.dataset.tdCrossSellSeedProductId || '');
      url.searchParams.set('limit', `${this.maxProducts}`);
      url.searchParams.set('intent', 'related');
      return url.toString();
    }

    extractSlideMarkup(html) {
      if (!html) return [];

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const cartProductIds = this.cartProductIds;
      const slideMarkup = [];

      doc.querySelectorAll('[data-td-cross-sell-slide]').forEach((slide) => {
        if (!(slide instanceof HTMLElement)) return;
        if (slideMarkup.length >= this.maxProducts) return;

        const productId = Number.parseInt(slide.dataset.tdCrossSellProductId || '', 10);
        if (Number.isFinite(productId) && cartProductIds.has(productId)) return;

        slideMarkup.push(slide.outerHTML);
      });

      return slideMarkup;
    }

    renderCardsFromMarkup(slideMarkup) {
      if (!(this.wrapper instanceof HTMLElement)) return;
      if (!Array.isArray(slideMarkup) || slideMarkup.length === 0) {
        this.wrapper.replaceChildren();
        this.setAttribute('hidden', '');
        return;
      }

      const parser = new DOMParser();
      const slideNodes = slideMarkup
        .map((markup) => {
          if (typeof markup !== 'string' || markup.trim() === '') return null;

          // Markup is trusted from our own same-origin recommendations section response.
          const doc = parser.parseFromString(markup, 'text/html');
          const slideNode = doc.body.firstElementChild;

          if (!(slideNode instanceof HTMLElement)) return null;
          if (!slideNode.matches('[data-td-cross-sell-slide]')) return null;

          return slideNode;
        })
        .filter((node) => node instanceof HTMLElement);

      this.wrapper.replaceChildren(...slideNodes);
      this.toggleAttribute('hidden', slideNodes.length === 0);

      if (slideNodes.length > 0) {
        this.initializeSwiper();
      }
    }

    initializeSwiper() {
      if (!(this.slider instanceof HTMLElement)) return;
      this.slider.dispatchEvent(new CustomEvent('reinitialize-swiper'));
    }

    trimCache() {
      if (this.cacheStore.size <= 12) return;

      const oldestKey = this.cacheStore.keys().next().value;
      if (oldestKey) this.cacheStore.delete(oldestKey);
    }

  }

  customElements.define('td-cart-cross-sell', TDCartCrossSell);
}
